import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import PageHeader from '../shared/PageHeader';
import { documentLibraryApi } from '../../api/facades';
import { trackEvent } from '../../analytics/track';

type Folder = {
  id: number;
  name: string;
  scope: 'private' | 'shared';
  parent_id: number | null;
};

type FileItem = {
  id: number;
  name: string;
  file_url?: string | null;
};

const inputClass =
  'rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-1.5 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const DocumentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [path, setPath] = React.useState<Folder[]>([]);
  const [currentFolder, setCurrentFolder] = React.useState<Folder | null>(null);
  const [newFolderName, setNewFolderName] = React.useState('');
  const [uploading, setUploading] = React.useState(false);

  const { data: rootsRes, isLoading: rootsLoading } = useQuery({
    queryKey: ['document-library-roots'],
    queryFn: () => documentLibraryApi.getRoots(),
  });
  const rootsRaw: any = rootsRes;
  const rootsData = rootsRaw?.data?.data ?? rootsRaw?.data ?? {};
  const privateRoot = rootsData.private_root as Folder | undefined;
  const sharedRoot = rootsData.shared_root as Folder | undefined;

  const currentFolderId = currentFolder?.id ?? null;

  const { data: contentsRes, isLoading: contentsLoading } = useQuery({
    queryKey: ['document-library-contents', currentFolderId],
    queryFn: () => documentLibraryApi.getFolderContents(currentFolderId as number),
    enabled: currentFolderId != null,
  });

  const contentsRaw: any = contentsRes;
  const contentsData = contentsRaw?.data?.data ?? contentsRaw?.data ?? null;
  const folders: Folder[] = contentsData?.folders ?? [];
  const files: FileItem[] = contentsData?.files ?? [];

  const createFolderMu = useMutation({
    mutationFn: (payload: { parentId: number; name: string }) =>
      documentLibraryApi.createFolder(payload.parentId, payload.name),
    onSuccess: (_res, payload) => {
      setNewFolderName('');
      if (currentFolderId != null) {
        queryClient.invalidateQueries({ queryKey: ['document-library-contents', currentFolderId] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['document-library-roots'] });
      }
      void trackEvent('folder_created', {
        parent_id: payload.parentId,
        name_length: payload.name.length,
      });
      toast.success('Folder created.');
    },
    onError: (e: any) => toast.error(e?.message || 'Could not create folder.'),
  });

  const deleteFolderMu = useMutation({
    mutationFn: (folderId: number) => documentLibraryApi.deleteFolder(folderId),
    onSuccess: (_res, folderId) => {
      if (currentFolderId != null) {
        queryClient.invalidateQueries({ queryKey: ['document-library-contents', currentFolderId] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['document-library-roots'] });
      }
      void trackEvent('folder_deleted', {
        folder_id: folderId,
      });
      toast.success('Folder deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Could not delete folder.'),
  });

  const deleteFileMu = useMutation({
    mutationFn: (fileId: number) => documentLibraryApi.deleteFile(fileId),
    onSuccess: (_res, fileId) => {
      if (currentFolderId != null) {
        queryClient.invalidateQueries({ queryKey: ['document-library-contents', currentFolderId] });
      }
      void trackEvent('file_deleted', {
        file_id: fileId,
        folder_id: currentFolderId,
      });
      toast.success('File deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Could not delete file.'),
  });

  const handleOpenRoot = (root: Folder | undefined | null) => {
    if (!root) return;
    setPath([root]);
    setCurrentFolder(root);
    void trackEvent('document_library_root_opened', {
      folder_id: root.id,
      scope: root.scope,
    });
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentFolder || !e.target.files || e.target.files.length === 0) return;
    const file = e.target.files[0];
    try {
      setUploading(true);
      await documentLibraryApi.uploadFile(currentFolder.id, file, file.name);
      queryClient.invalidateQueries({ queryKey: ['document-library-contents', currentFolder.id] });
      void trackEvent('file_uploaded', {
        folder_id: currentFolder.id,
        file_name_length: file.name.length,
        file_size: file.size,
      });
      toast.success('File uploaded.');
    } catch (err: any) {
      toast.error(err?.message || 'Upload failed.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const loading = rootsLoading || (currentFolderId != null && contentsLoading);

  return (
    <div>
      <PageHeader
        hideTitle
        title="Documents"
        subtitle="Manage your private and shared document library."
      />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        {loading && <p className="text-sm text-slate-500">Loading…</p>}

        {!loading && !currentFolder && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              type="button"
              className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-left hover:bg-gray-50 dark:hover:bg-slate-800/80"
              onClick={() => handleOpenRoot(privateRoot ?? null)}
            >
              <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
                My documents
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Private space only visible to you.
              </p>
            </button>
            <button
              type="button"
              className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-left hover:bg-gray-50 dark:hover:bg-slate-800/80"
              onClick={() => handleOpenRoot(sharedRoot ?? null)}
            >
              <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
                Shared
              </h3>
              <p className="text-sm text-slate-500 mt-1">
                Shared folders and files visible to your team.
              </p>
            </button>
          </div>
        )}

        {!loading && currentFolder && (
          <>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <nav className="text-xs text-slate-500" aria-label="Breadcrumb">
                  <ol className="inline-flex items-center gap-1">
                    <li>
                      <button
                        type="button"
                        className="text-primary-700 hover:text-primary-800"
                        onClick={() => {
                          setPath([]);
                          setCurrentFolder(null);
                        }}
                      >
                        Documents
                      </button>
                    </li>
                    {path.map((folder, idx) => (
                      <li key={folder.id} className="inline-flex items-center gap-1">
                        <span className="text-slate-400">{'>'}</span>
                        <button
                          type="button"
                          className={`hover:text-primary-800 ${
                            idx === path.length - 1
                              ? 'text-slate-700 dark:text-slate-100 font-semibold'
                              : 'text-primary-700'
                          }`}
                          onClick={() => {
                            setPath(path.slice(0, idx + 1));
                            setCurrentFolder(folder);
                          }}
                        >
                          {folder.name}
                        </button>
                      </li>
                    ))}
                  </ol>
                </nav>
              </div>
              <div className="flex flex-wrap gap-2">
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!newFolderName.trim() || !currentFolder) return;
                    createFolderMu.mutate({
                      parentId: currentFolder.id,
                      name: newFolderName.trim(),
                    });
                  }}
                  className="flex items-center gap-2"
                >
                  <input
                    className={inputClass}
                    placeholder="New folder name"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                  />
                  <button
                    type="submit"
                    className="inline-flex items-center rounded-lg bg-primary-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-800"
                    disabled={createFolderMu.isPending || !newFolderName.trim()}
                  >
                    {createFolderMu.isPending ? 'Creating…' : 'New folder'}
                  </button>
                </form>
                <label className="inline-flex items-center rounded-lg bg-slate-100 dark:bg-slate-700 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-100 cursor-pointer">
                  <span>{uploading ? 'Uploading…' : 'Upload file'}</span>
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleUpload}
                    disabled={uploading}
                  />
                </label>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">
                  Folders
                </h3>
                {folders.length === 0 ? (
                  <p className="text-sm text-slate-500">No subfolders.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {folders.map((f) => (
                      <button
                        key={f.id}
                        type="button"
                        className="border border-gray-100 dark:border-gray-800 rounded-2xl p-3 text-left hover:shadow-sm hover:border-primary-200 dark:hover:border-primary-400 flex flex-col justify-between"
                        onClick={() => {
                          setPath((prev) => [...prev, f]);
                          setCurrentFolder(f);
                          void trackEvent('folder_opened', {
                            folder_id: f.id,
                            scope: f.scope,
                            parent_id: f.parent_id,
                          });
                        }}
                      >
                        <div>
                          <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary-50 text-primary-700 mb-2">
                            <span className="text-base">📁</span>
                          </div>
                          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                            {f.name}
                          </div>
                          <div className="text-xs text-slate-500 mt-1">
                            {f.scope === 'private' ? 'Private folder' : 'Shared folder'}
                          </div>
                        </div>
                        <div className="mt-2 flex justify-end">
                          <button
                            type="button"
                            className="text-[11px] text-red-600 hover:text-red-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              const ok = window.confirm(
                                'Delete this folder and all its contents? This cannot be undone.',
                              );
                              if (!ok) return;
                              deleteFolderMu.mutate(f.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase mb-2">
                  Files
                </h3>
                {files.length === 0 ? (
                  <p className="text-sm text-slate-500">No files in this folder.</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {files.map((file) => (
                      <div
                        key={file.id}
                        className="border border-gray-100 dark:border-gray-800 rounded-2xl p-3 flex flex-col justify-between hover:shadow-sm hover:border-primary-200 dark:hover:border-primary-400"
                      >
                        <button
                          type="button"
                          className="text-left flex flex-col flex-1"
                          onClick={() => {
                            void trackEvent('file_opened', {
                              file_id: file.id,
                              folder_id: currentFolderId,
                            });
                            navigate(`/dashboard/documents/${file.id}`);
                          }}
                        >
                          <div className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-sky-50 text-sky-700 mb-2">
                            <span className="text-base">📄</span>
                          </div>
                          <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
                            {file.name}
                          </div>
                          <div className="mt-1 text-xs text-slate-500 space-y-0.5">
                            <div>File</div>
                          </div>
                        </button>
                        <div className="mt-2 flex justify-end">
                          <button
                            type="button"
                            className="text-[11px] text-red-600 hover:text-red-700"
                            onClick={() => {
                              const ok = window.confirm(
                                'Delete this file? This cannot be undone.',
                              );
                              if (!ok) return;
                              deleteFileMu.mutate(file.id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentsListPage;

